/**
 * You › Security: "Lock Viko when…" (per device) with the live lock signal, drillable to each
 * source (browser idle detection, vikod on this computer, fallback timers).
 */
import { Button } from '@viko/transcript-ui'
import { Lock } from 'lucide-react'
import type { AppLockStatus } from '../lib/app-lock-controller.js'
import {
  BACKGROUND_CHOICES_MS,
  HIDDEN_FALLBACK_MS,
  IDLE_CHOICES_MS,
  type LockSource,
} from '../lib/idle-lock.js'
import { PRESENCE_STALE_MS } from '../lib/lock-signals.js'
import { useMachines } from '../lib/queries.js'
import { browserMatchesPlatform } from '../routes/enroll.js'
import { useAppLockStatus } from './AppLock.js'
import { PresenceUnlockRow } from './PresenceUnlockRow.js'

export const duration = (ms: number) =>
  ms % 3_600_000 === 0 ? `${ms / 3_600_000} h` : `${Math.round(ms / 60_000)} min`

export function sourceText(s: AppLockStatus, machineName?: string): string {
  const p = s.policy
  const by: Record<LockSource, string> = {
    daemon: `Following ${machineName ?? 'this computer'}’s screen lock and idle (via viko)`,
    'idle-detector': 'Following this computer’s screen lock and idle (via the browser)',
    fallback: `No lock signal: after ${duration(p.idleMs)} idle in Viko or ${duration(HIDDEN_FALLBACK_MS)} hidden`,
    idle: `After ${duration(p.idleMs)} idle in Viko`,
    background: `After ${duration(p.backgroundMs)} in the background`,
  }
  return by[s.source]
}

function DurationSelect({
  label,
  value,
  choices,
  onChange,
}: {
  label: string
  value: number
  choices: number[]
  onChange: (ms: number) => void
}) {
  const options = choices.includes(value) ? choices : [...choices, value].sort((a, b) => a - b)
  return (
    <select
      aria-label={label}
      className="input !min-h-9 !w-auto !py-1"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {options.map((ms) => (
        <option key={ms} value={ms}>
          {duration(ms)}
        </option>
      ))}
    </select>
  )
}

export function LockSettings() {
  const { controller: c, status: s } = useAppLockStatus()
  const machines = useMachines()
  if (!c || !s) return <p className="m-0 px-4 text-[14px] text-text-3">App lock is starting…</p>
  const p = s.policy
  const local = (machines.data ?? []).filter(
    (m) => m.id === s.localMachine || browserMatchesPlatform(m.platform),
  )
  const localName = machines.data?.find((m) => m.id === s.localMachine)?.name
  const idleText =
    s.idlePermission === 'granted'
      ? s.source === 'idle-detector' || s.source === 'daemon'
        ? 'On'
        : 'Allowed'
      : s.idlePermission === 'denied'
        ? 'Blocked in this browser’s site settings'
        : s.idlePermission === 'prompt'
          ? 'Not allowed yet'
          : 'Not supported in this browser'
  const presence = s.presence
  const daemonText = !s.localMachine
    ? 'No machine set for this computer'
    : presence?.available
      ? `Live: screen ${presence.screen ?? 'unknown'}`
      : `No report in the last ${duration(PRESENCE_STALE_MS)} (viko offline or not on macOS/Windows)`
  return (
    <div className="grid gap-3 px-4 pb-2">
      <p className="m-0 text-[15px] font-medium">Lock Viko when…</p>
      {s.mobile ? (
        <div className="flex flex-wrap items-center gap-3 text-[15px]">
          <span className="flex-1">It has been in the background for</span>
          <DurationSelect
            label="Background time before locking"
            value={p.backgroundMs}
            choices={BACKGROUND_CHOICES_MS}
            onChange={(backgroundMs) => void c.setPolicy({ ...p, backgroundMs })}
          />
        </div>
      ) : (
        <>
          <div className="choice" role="radiogroup" aria-label="Lock Viko when">
            <button
              type="button"
              role="radio"
              aria-checked={p.desktop === 'device'}
              onClick={() => void c.setPolicy({ ...p, desktop: 'device' })}
            >
              This computer locks or sleeps
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={p.desktop === 'idle'}
              onClick={() => void c.setPolicy({ ...p, desktop: 'idle' })}
            >
              Viko is idle
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[15px]">
            <span className="flex-1">
              {p.desktop === 'idle'
                ? 'Idle time in Viko'
                : 'Without a lock signal, idle time in Viko'}
            </span>
            <DurationSelect
              label="Idle time before locking"
              value={p.idleMs}
              choices={IDLE_CHOICES_MS}
              onChange={(idleMs) => void c.setPolicy({ ...p, idleMs })}
            />
          </div>
        </>
      )}
      <details className="st-lease text-[14px]">
        <summary className="cursor-pointer">
          <span className="font-medium">Now: </span>
          {sourceText(s, localName)}
        </summary>
        {s.mobile ? (
          <p className="m-0 mt-2 text-text-2">
            On phones Viko locks after time in the background. Your phone’s own lock screen covers
            the rest.
          </p>
        ) : (
          <dl className="m-0 mt-2 grid gap-2">
            <div>
              <dt className="font-medium">Browser idle detection</dt>
              <dd className="m-0 flex flex-wrap items-center gap-2 text-text-2">
                {idleText}
                {s.idlePermission === 'prompt' ? (
                  <Button size="sm" onClick={() => void c.allowIdleDetection()}>
                    Allow
                  </Button>
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="font-medium">viko on this computer</dt>
              <dd className="m-0 grid gap-1 text-text-2">
                <span>{daemonText}</span>
                <select
                  aria-label="This computer is"
                  className="input !min-h-9 !w-auto !py-1"
                  value={s.localMachine ?? ''}
                  onChange={(e) => void c.setLocalMachine(e.target.value || undefined)}
                >
                  <option value="">Not one of my machines</option>
                  {local.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            {s.localMachine ? (
              <PresenceUnlockRow
                machineId={s.localMachine}
                {...(localName ? { name: localName } : {})}
              />
            ) : null}
            <div>
              <dt className="font-medium">Fallback</dt>
              <dd className="m-0 text-text-2">
                With neither signal: after {duration(p.idleMs)} idle in Viko, or{' '}
                {duration(HIDDEN_FALLBACK_MS)} hidden. A 30 min gap (sleep) always locks.
              </dd>
            </div>
          </dl>
        )}
      </details>
      <div>
        <Button size="sm" icon={<Lock size={14} />} onClick={() => c.lock.lock()}>
          Lock now
        </Button>
      </div>
    </div>
  )
}
